import { FC, lazy, Suspense, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card, FlexContent, Tab, Tabs } from "@rothoblaas/design-system";
import Header from "../../components/ui/Header";
import ReadOnlyGate from "../../components/ui/ReadOnlyGate";
import useI18n from "../../hooks/useTranslations";
import { isProjectReadNotAllowed } from "../../http/errors";
import { useProjectsApi } from "../../http/useProjects";
import ProjectCalculations from "./ProjectCalculations";
import { ProjectForm } from "./ProjectForm";

// only for a project not shared with the user
const NotSharedPage = lazy(() => import("../../components/sharing/NotSharedPage"));

interface ProjectProps {
  isAdmin?: boolean;
}

const Project: FC<ProjectProps> = ({ isAdmin }: ProjectProps) => {
  const i18n = useI18n();
  const { projectId } = useParams<{ projectId: string }>();
  const projectsApi = useProjectsApi();

  const aborter = useRef(new AbortController());

  const projectSwr = projectsApi.getProjectById(aborter.current.signal, isAdmin);

  useEffect(() => {
    projectId && projectSwr.trigger(projectId);
  }, [projectId]);

  if (!projectSwr.data && !projectSwr.error) {
    return null;
  }

  if (!isAdmin && isProjectReadNotAllowed(projectSwr.error)) {
    return (
      <Suspense fallback={null}>
        <NotSharedPage resource="projects" resourceId={projectId!} />
      </Suspense>
    );
  }

  const canEdit = projectSwr.data?.canEdit ?? false;

  const tabs: Tab[] = [{ title: i18n.translation.common.details }, { title: i18n.translation.calculations.plural }];

  return (
    <ReadOnlyGate canEdit={canEdit}>
      <Header
        title={projectSwr.data?.name ?? i18n.translation.common.loading}
        error={projectSwr.error}
        path={[
          {
            display: isAdmin ? i18n.translation.projects.all : i18n.translation.projects.my,
            url: isAdmin ? "/admin/projects" : "/my-projects",
          },
          { display: projectSwr.data?.name ?? "" },
        ]}
      />

      <Tabs renderMode="eager"
        urlParameter="tab"
        tabs={tabs}
        contents={[
          <FlexContent padding direction="row">
            <Card shadow>
              <ProjectForm
                project={!projectSwr.error ? projectSwr.data : undefined}
                disabled={projectSwr.isMutating || !!projectSwr.error || !canEdit}
                isLoading={projectSwr.isMutating}
                disableNavigationOnSuccess
              />
            </Card>
          </FlexContent>,
          <ProjectCalculations projectId={projectId!} isAdmin={isAdmin} disabled={!!projectSwr.error} canEdit={canEdit} />,
        ]}
      />
    </ReadOnlyGate>
  );
};

export default Project;
